import { formatDate } from '@/utils/format/date';
import type { TmmdeEquipmentRow } from '@/services/calibration.service';

export type TmmdeLabelVariant = 'STATUS' | 'NOT_FOR_USE';

export type TmmdeLabelModel = {
  variant: TmmdeLabelVariant;
  heading: string;
  tone: 'ok' | 'warn' | 'stop';
  rows: Array<{ label: string; value: string }>;
  /** Limitation / reason line shown in a band under the rows. */
  note?: string;
  qrPayload: string;
};

const KIND_HEADING: Record<string, string> = {
  VERIFICATION: 'VERIFIED',
  SOFTWARE_VALIDATION: 'VALIDATED',
};

export function tmmdeLabelQrPayload(assetId: string, origin?: string): string {
  const path = `/qms/tmmde/${assetId}`;
  return origin ? `${origin.replace(/\/$/, '')}${path}` : path;
}

/**
 * Equipment identification label (calibration status, or NOT FOR USE).
 * Carries asset identity and calibration dates only — no document number.
 */
export function buildTmmdeLabel(
  eq: TmmdeEquipmentRow,
  variant: TmmdeLabelVariant,
  opts: { origin?: string; calibratedBy?: string | null; reason?: string | null; date?: string | null } = {}
): TmmdeLabelModel {
  const qrPayload = tmmdeLabelQrPayload(eq.id, opts.origin);
  const identity = [
    { label: 'Equipment', value: eq.asset_name || '—' },
    { label: 'ID', value: eq.asset_code || '—' },
    ...(eq.serial_number ? [{ label: 'S/N', value: String(eq.serial_number) }] : []),
  ];

  if (variant === 'NOT_FOR_USE') {
    const reason = (opts.reason ?? eq.not_for_use_reason ?? '').trim();
    return {
      variant,
      heading: 'NOT FOR USE',
      tone: 'stop',
      rows: [...identity, { label: 'Date', value: formatDate(opts.date ?? new Date(), { nullDisplay: '—' }) }],
      note: reason || undefined,
      qrPayload,
    };
  }

  const firstUsePending = eq.interval_basis === 'FIRST_USE' && !eq.calibration_due_date;
  const due = firstUsePending
    ? `${eq.calibration_frequency_days ?? '—'} days from first use`
    : formatDate(eq.calibration_due_date, { nullDisplay: '—' });
  const calibratedBy = (opts.calibratedBy ?? eq.supplier_name ?? eq.calibration_provider ?? '').trim();

  const limitation = String(eq.calibration_status) === 'CALIBRATED' || String(eq.calibration_status) === 'DUE_SOON'
    ? undefined
    : 'Status not valid for use — check equipment list';

  return {
    variant,
    heading: KIND_HEADING[String(eq.calibration_kind)] || 'CALIBRATED',
    tone: limitation ? 'warn' : 'ok',
    rows: [
      ...identity,
      { label: 'Cal. date', value: formatDate(eq.last_calibration_date, { nullDisplay: '—' }) },
      { label: 'Due date', value: due },
      ...(calibratedBy ? [{ label: 'By', value: calibratedBy }] : []),
    ],
    note: limitation,
    qrPayload,
  };
}
